import { useEffect, useRef } from 'react'

import { useAiChat } from 'modules/ai/chat/useAiChat'

import { OpenOneShiftReplacementShiftDocument } from 'generated/resources/graphql'

import { useOne, useOneFeatureAvailable } from 'modules/ai/lib/useOne'
import { useActivity } from 'modules/core/lib/activities/activity'
import { useQuery } from 'modules/core/hooks/useQuery'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

type Props = {
  disruptedShiftId: string
}

const formatShiftDate = (localStartAt: string): string => {
  const date = stringToDate(localStartAt, 'backend_datetime_without_timezone')
  return `${i18nDateToString(date, 'ui_long')} ${i18nDateToString(date, 'ui_time')}`
}

export const OpenOneShiftReplacementActivity = ({ disruptedShiftId }: Props) => {
  const oneAvailable = useOneFeatureAvailable()
  const { open, closeActivity } = useActivity()
  const { open: isChatOpen, clearAndAppend } = useAiChat()
  const hasInjectedGreetingRef = useRef(false)

  const { data: shiftData } = useQuery(OpenOneShiftReplacementShiftDocument, {
    variables: { shiftIds: [disruptedShiftId] },
    skip: !oneAvailable,
    fetchPolicy: 'cache-first',
  })

  const shift = shiftData?.shiftManagement.shifts?.[0]

  useEffect(() => {
    if (!oneAvailable || !isChatOpen || !shift || hasInjectedGreetingRef.current) {
      return
    }

    const employeeName = shift.employee?.fullName
    const localStartAt = shift.localStartAt

    if (!employeeName || !localStartAt) {
      return
    }

    hasInjectedGreetingRef.current = true

    clearAndAppend([
      {
        role: 'assistant',
        content: i18n.t('shift_management.one.shift_replacement_greeting', {
          name: employeeName,
          date: formatShiftDate(localStartAt),
        }),
      },
    ])

    closeActivity()
  }, [clearAndAppend, closeActivity, isChatOpen, oneAvailable, shift])

  useOne(
    ['shiftManagement'],
    { disruptedShiftId, source: 'shift_replacement_notification' },
    {
      autoOpenChat: true,
      coCreationKey: `shift-replacement-${disruptedShiftId}`,
      enabled: oneAvailable,
    }
  )

  if (!open) {
    return null
  }

  return null
}
